"use client";

import { forwardRef, useMemo } from "react";
import { Braces, Plus } from "lucide-react";
import { createChipExtension } from "./chip-extension";
import { createChipSuggestion, type ChipListHandle } from "./chip-suggestion";
import { SuggestionListContainer, SuggestionRow, useSuggestionListNav } from "./suggestion-list";
import type { ChipTypeStyle } from "./styles";

// The `{{` variable-chip family: merge-tag chips bound to a form's field
// definitions (task templates today). Uses the same chip node as the AOP
// editor's entity chips, with refType "variable" and the field key as both id
// and label.

/** Chip refType for template variables. */
export const VARIABLE_REF_TYPE = "variable";

/** Chip styling for the variable family (green, braces icon). */
export const VARIABLE_CHIP_STYLES: Record<string, ChipTypeStyle> = {
  [VARIABLE_REF_TYPE]: {
    color: "#166534",
    background: "oklch(0.65 0.18 150 / 0.14)",
    iconSvg:
      "%3Cpath d='M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1'/%3E%3Cpath d='M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1'/%3E",
  },
};

/** A form-field variable insertable from the `{{` popup. */
export interface TemplateVariable {
  /** Slug key, e.g. "party_name" — the chip's id and rendered text. */
  key: string;
  /** Human-readable label, e.g. "Party name" (shown in the popup). */
  label: string;
}

/**
 * Normalize free text into a variable key: "Party name" → "party_name".
 * Emits only server-legal keys (`/^[a-z][a-z0-9_]*$/`, ≤ 64 characters):
 * leading characters are dropped until the key starts with a letter, so
 * text with no letters slugs to "" (callers treat that as "no key").
 */
export function slugifyVariableKey(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .slice(0, 64)
    .replace(/_+$/, "");
}

// ============================================================================
// Popup list
// ============================================================================

interface VariableChipListProps {
  items: TemplateVariable[];
  command: (attrs: { id: string; label: string; refType: string }) => void;
  /** Current `{{` query. Drives the "Create field" action row. */
  query?: string;
  /**
   * When set, an unknown query offers a trailing "Create field '<key>'"
   * action. Should register the field and return the created variable (the
   * chip is then inserted), or null to abort.
   */
  onCreateVariable?: (key: string) => TemplateVariable | null;
}

/** A selectable row in the popup: an existing variable or the create action. */
type Selectable = { kind: "item"; item: TemplateVariable } | { kind: "create"; key: string };

export const VariableChipList = forwardRef<ChipListHandle, VariableChipListProps>(
  ({ items, command, query, onCreateVariable }, ref) => {
    // Offer "Create field" when the query slugs to a non-empty key that isn't
    // already a field — the design-doc affordance for inserting an unknown tag.
    const createKey = useMemo(() => {
      if (!onCreateVariable) return null;
      const key = slugifyVariableKey(query ?? "");
      if (!key) return null;
      return items.some((item) => item.key === key) ? null : key;
    }, [items, query, onCreateVariable]);

    const selectables = useMemo(() => {
      const out: Selectable[] = items.map((item) => ({ kind: "item" as const, item }));
      if (createKey) out.push({ kind: "create", key: createKey });
      return out;
    }, [items, createKey]);

    const insert = (variable: TemplateVariable): void => {
      command({ id: variable.key, label: variable.key, refType: VARIABLE_REF_TYPE });
    };

    const selectItem = (index: number): void => {
      const row = selectables[index];
      if (!row) return;
      if (row.kind === "create") {
        const created = onCreateVariable?.(row.key);
        if (created) insert(created);
        return;
      }
      insert(row.item);
    };

    const { selectedIndex, setSelectedIndex, itemRef } = useSuggestionListNav<HTMLButtonElement>(
      ref,
      selectables.length,
      selectItem,
      selectables,
    );

    return (
      <SuggestionListContainer
        className="variable-chip-popup w-72"
        empty={selectables.length === 0}
        emptyMessage="No fields"
      >
        {items.length > 0 && (
          <div className="px-2 pt-1.5 pb-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
            Fields
          </div>
        )}
        {items.map((item, index) => (
          <SuggestionRow
            key={item.key}
            index={index}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            itemRef={itemRef}
            onSelect={selectItem}
          >
            <Braces className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium">{item.label}</span>
            <span className="ml-auto truncate font-mono text-xs text-muted-foreground">
              {item.key}
            </span>
          </SuggestionRow>
        ))}
        {createKey && (
          <SuggestionRow
            index={items.length}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            itemRef={itemRef}
            onSelect={selectItem}
            muted
          >
            <Plus className="size-3.5 shrink-0" />
            <span className="font-medium">Create field &lsquo;{createKey}&rsquo;</span>
          </SuggestionRow>
        )}
      </SuggestionListContainer>
    );
  },
);

VariableChipList.displayName = "VariableChipList";

// ============================================================================
// Configured extension
// ============================================================================

/**
 * Chip node configured for `{{` variable insertion. `getVariables` /
 * `getOnCreateVariable` are read per-keystroke (pass refs' getters so the
 * editor isn't rebuilt when fields change).
 */
export function createVariableChipExtension(opts: {
  getVariables: () => TemplateVariable[];
  getOnCreateVariable?: () => ((key: string) => TemplateVariable | null) | undefined;
}) {
  return createChipExtension({
    suggestion: createChipSuggestion<TemplateVariable, VariableChipListProps>({
      char: "{{",
      items: (query) => {
        const q = query.trim().toLowerCase();
        const variables = opts.getVariables();
        if (!q) return variables;
        return variables.filter(
          (variable) =>
            variable.key.toLowerCase().includes(q) || variable.label.toLowerCase().includes(q),
        );
      },
      component: VariableChipList,
      componentProps: (props) => {
        const onCreateVariable = opts.getOnCreateVariable?.();
        return {
          items: props.items,
          command: props.command,
          query: props.query,
          ...(onCreateVariable ? { onCreateVariable } : {}),
        };
      },
    }),
  });
}
